/**
 * useActivationProgress — derives the studio's "operating system"
 * activation arc from data already cached by other React Query
 * hooks. No extra round-trips: each underlying hook is shared with
 * the rest of the dashboard so the cache is warm by the time this
 * runs.
 */
import { useMemo } from 'react';
import { useOrganization } from '@/services/organizations';
import { useInquiries } from '@/services/inquiries';
import { useClients } from '@/services/clients';
import { useProjects } from '@/services/projects';
import { useIntakeForms } from '@/services/intakeForms';
import { useBookingLinks } from '@/services/scheduler';
import { useConnectAccount } from '@/services/stripeConnect';
import { useSiteRowV2, isSiteV2Live } from '@/services/sitesV2';
import { useCreativeRole } from '@/hooks/useCreativeRole';

export type ActivationStepId =
  | 'profile'
  | 'brand'
  | 'intake_form'
  | 'scheduling'
  | 'payments'
  | 'studio_site'
  | 'first_inquiry'
  | 'first_client'
  | 'first_project';

export interface ActivationStep {
  id: ActivationStepId;
  label: string;
  hint: string;
  href: string;
  ctaLabel: string;
  completed: boolean;
}

const DEFAULT_BRAND = '#C9A16B';

/**
 * ONB-3 — map the personalization module's `activationPriorityId`
 * (photographer → 'watermark', videographer → 'mux', etc.) onto the
 * actual step id we surface. Some priority ids ('watermark', 'mux',
 * 'contract') don't exist as standalone activation steps yet — we
 * route them to the closest existing step so the reorder still
 * biases the checklist toward the studio's craft:
 *
 *   watermark → studio_site  (watermark lives inside Sites setup)
 *   mux       → studio_site  (Mux processing = ship the site + deliveries)
 *   booking   → scheduling
 *   contract  → intake_form  (contracts + inquiries share the intake flow)
 *   client    → first_client
 *
 * When a priority id has no mapping we leave the default ordering
 * alone — a safe no-op.
 */
const PRIORITY_TO_STEP: Record<string, ActivationStepId> = {
  watermark: 'studio_site',
  mux: 'studio_site',
  booking: 'scheduling',
  contract: 'intake_form',
  client: 'first_client',
};

export function useActivationProgress() {
  const { data: organization } = useOrganization();
  const { data: inquiries = [] } = useInquiries();
  const { data: clients = [] } = useClients();
  const { data: projects = [] } = useProjects();
  const { data: intakeForms = [] } = useIntakeForms();
  const { data: bookingLinks = [] } = useBookingLinks();
  const { data: connectAccount } = useConnectAccount();
  const { data: studioSite } = useSiteRowV2();
  // ONB-3 — the persisted creative role drives which incomplete
  // step gets folded to the top of the checklist. Any completed
  // step is left in place so the studio's actual progress reads
  // linearly; only the still-open steps get reshuffled.
  const { personalization } = useCreativeRole();

  const steps = useMemo<ActivationStep[]>(() => {
    const org = organization as
      | (Record<string, any> & {
          name?: string | null;
          business_type?: string | null;
          logo_url?: string | null;
          brand_color?: string | null;
          studio_label?: string | null;
        })
      | undefined;

    const studioLabel = org?.studio_label?.trim();
    const brandCustomized =
      Boolean(org?.logo_url) ||
      (org?.brand_color && org.brand_color !== DEFAULT_BRAND) ||
      (Boolean(studioLabel) && studioLabel !== 'Studio');

    return [
      {
        id: 'profile',
        label: 'Set up your business profile',
        hint: 'Name, business type, the basics — Orbit needs to know who you are.',
        href: '/settings?tab=organization',
        ctaLabel: 'Open profile',
        completed: Boolean(org?.business_type && org?.name?.trim()),
      },
      {
        id: 'brand',
        label: 'Make it yours',
        hint: 'Logo, accent color, what you call yourself — every public surface inherits this.',
        href: '/settings?tab=organization',
        ctaLabel: 'Add brand',
        completed: Boolean(brandCustomized),
      },
      {
        id: 'intake_form',
        label: 'Create an intake form',
        hint: 'The first surface clients see — collect inquiries the right way.',
        href: '/settings?tab=intake-forms',
        ctaLabel: 'Build form',
        completed: intakeForms.length > 0,
      },
      {
        id: 'scheduling',
        label: 'Set up scheduling',
        hint: 'Bookable links so clients can grab time without the back-and-forth.',
        href: '/settings?tab=scheduling',
        ctaLabel: 'Open scheduling',
        completed: bookingLinks.length > 0,
      },
      {
        id: 'payments',
        label: 'Connect payments',
        hint: 'Stripe Connect — invoices and deposits land directly in your account.',
        href: '/settings?tab=payments',
        ctaLabel: 'Connect Stripe',
        completed: Boolean(connectAccount?.charges_enabled),
      },
      {
        id: 'studio_site',
        label: 'Publish your Business Site',
        hint: 'A public home for your work — built in, no separate website needed.',
        href: '/sites',
        ctaLabel: 'Publish site',
        completed: isSiteV2Live(studioSite),
      },
      {
        id: 'first_inquiry',
        label: 'Receive your first inquiry',
        hint: 'Share your form. The first one always feels real.',
        href: '/inquiries',
        ctaLabel: 'View inquiries',
        completed: inquiries.length > 0,
      },
      {
        id: 'first_client',
        label: 'Add your first client',
        hint: 'A name and an email — that\u2019s a relationship Orbit can track.',
        href: '/clients',
        ctaLabel: 'Add client',
        completed: clients.length > 0,
      },
      {
        id: 'first_project',
        label: 'Create your first project',
        hint: 'Where the work actually happens — files, milestones, deliverables.',
        href: '/projects',
        ctaLabel: 'Start project',
        completed: projects.length > 0,
      },
    ];
  }, [organization, inquiries, clients, projects, intakeForms, bookingLinks, connectAccount, studioSite]);

  // ONB-3 — bias the checklist order toward the studio's craft.
  // Completed steps stay in their original position so progress
  // reads linearly; only incomplete steps get reshuffled, with the
  // role's priority step folded to the front of the incomplete
  // block. That way a photographer sees "Publish your Business Site"
  // (their watermark home) before "Set up scheduling", but a wedding
  // studio that's already shipped their site still sees the natural
  // next step surface.
  const orderedSteps = useMemo<ActivationStep[]>(() => {
    const priorityStepId = PRIORITY_TO_STEP[personalization.activationPriorityId];
    if (!priorityStepId) return steps;
    const priorityStep = steps.find((s) => s.id === priorityStepId);
    // Only reorder if the priority step exists AND is incomplete —
    // don't shuffle a done step back into the "next up" position.
    if (!priorityStep || priorityStep.completed) return steps;
    // Build the new list: keep completed steps in place, drop the
    // priority step into the first-incomplete slot, then append the
    // remaining incomplete steps in their original order.
    const out: ActivationStep[] = [];
    let priorityInserted = false;
    for (const step of steps) {
      if (step.completed) {
        out.push(step);
        continue;
      }
      if (!priorityInserted) {
        out.push(priorityStep);
        priorityInserted = true;
        if (step.id === priorityStep.id) continue;
      }
      if (step.id === priorityStep.id) continue;
      out.push(step);
    }
    return out;
  }, [steps, personalization.activationPriorityId]);

  const completedCount = orderedSteps.filter((s) => s.completed).length;
  const totalCount = orderedSteps.length;
  const fraction = totalCount === 0 ? 0 : completedCount / totalCount;
  const isFullyActivated = completedCount === totalCount;
  const nextStep = orderedSteps.find((s) => !s.completed) ?? null;

  return { steps: orderedSteps, completedCount, totalCount, fraction, isFullyActivated, nextStep };
}

export default useActivationProgress;